/** Named, locale-agnostic format intents. Each maps to Intl.DateTimeFormat options. */
export type DateTimeFormatName =
  | "MonthDayYear"
  | "MonthDayYearLong"
  | "MonthDay"
  | "MonthDayLong"
  | "WeekdayMonthDayYear"
  | "WeekdayMonthDay"
  | "WeekdayLong"
  | "WeekdayShort"
  | "DayOnly"
  | "TimeShort"
  | "TimeShortWithZone"
  | "MonthDayTime"
  | "MonthDayTimeWithZone"
  | "MonthDayYearTime"
  | "MonthDayYearTimeShort"
  | "WeekdayShortMonthDay"
  | "WeekdayShortMonthDayYear"
  | "WeekdayLongMonthDay"
  | "MonthLongYear"
  | "MonthShort"
  | "MonthLong"
  | "NumericDate"
  | "YearNumeric";

const dateTimeFormatCatalog = <T extends Record<DateTimeFormatName, Intl.DateTimeFormatOptions>>(
  catalog: T,
): Record<DateTimeFormatName, Intl.DateTimeFormatOptions> => catalog;
const dateFnsPatternCatalog = <T extends Record<string, DateTimeFormatName>>(
  catalog: T,
): Record<string, DateTimeFormatName> => catalog;

export const DATE_TIME_FORMATS = dateTimeFormatCatalog({
  MonthDayYear: { month: "short", day: "numeric", year: "numeric" },
  MonthDayYearLong: { month: "long", day: "numeric", year: "numeric" },
  MonthDay: { month: "short", day: "numeric" },
  MonthDayLong: { month: "long", day: "numeric" },
  WeekdayMonthDayYear: {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  },
  WeekdayMonthDay: { weekday: "long", month: "long", day: "numeric" },
  WeekdayLong: { weekday: "long" },
  WeekdayShort: { weekday: "short" },
  DayOnly: { day: "numeric" },
  TimeShort: { hour: "numeric", minute: "2-digit" },
  TimeShortWithZone: {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  },
  MonthDayTime: {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  MonthDayTimeWithZone: {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  },
  MonthDayYearTime: {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  MonthDayYearTimeShort: {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  WeekdayShortMonthDay: { weekday: "short", month: "short", day: "numeric" },
  WeekdayShortMonthDayYear: {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  },
  WeekdayLongMonthDay: { weekday: "long", month: "long", day: "numeric" },
  MonthLongYear: { month: "long", year: "numeric" },
  // Month-only display intents. Used directly (NOT via DATE_FNS_PATTERN_TO_FORMAT_NAME)
  // because the "MMM"/"MMMM" date-fns patterns are reused as MACHINE inputs (Slack channel
  // names) and must not be bridged. Callsites that render a month by itself call these names.
  MonthShort: { month: "short" },
  MonthLong: { month: "long" },
  NumericDate: { year: "numeric", month: "numeric", day: "numeric" },
  YearNumeric: { year: "numeric" },
});

/**
 * Bridge from the date-fns format strings currently used in the codebase to catalog names.
 * This lets existing helpers keep their string params while routing through Intl.
 * Add a new entry (and catalog name) when a new display format appears — never invent
 * an ad-hoc token string at a callsite.
 */
export const DATE_FNS_PATTERN_TO_FORMAT_NAME = dateFnsPatternCatalog({
  "MMM d, yyyy": "MonthDayYear",
  "MMM dd, yyyy": "MonthDayYear",
  "MMMM d, yyyy": "MonthDayYearLong",
  "LLLL d, yyyy": "MonthDayYearLong",
  "MMM d": "MonthDay",
  "MMMM d": "MonthDayLong",
  "EEEE, MMMM d, yyyy": "WeekdayMonthDayYear",
  "EEEE, MMMM d": "WeekdayMonthDay",
  EEEE: "WeekdayLong",
  EEE: "WeekdayShort",
  d: "DayOnly",
  "h:mm a": "TimeShort",
  "h:mm aaa": "TimeShort",
  "h:mma": "TimeShort",
  "MMM d, h:mm aaa": "MonthDayTime",
  "MMM d, h:mm a zzz": "MonthDayTimeWithZone",
  "MMMM d, yyyy h:mm aaa": "MonthDayYearTime",
  "MMM d, yyyy, h:mm aaa": "MonthDayYearTimeShort",
  "MMM d yyyy, h:mm a": "MonthDayYearTimeShort",
  "MMM d, yyyy hh:mm a": "MonthDayYearTimeShort",
  "MMM d, hh:mm a": "MonthDayTime",
  "EEE, LLL d": "WeekdayShortMonthDay",
  "E, MMM d": "WeekdayShortMonthDay",
  "MMM d, yyyy, E": "WeekdayShortMonthDayYear",
  "MMMM d, EEEE": "WeekdayLongMonthDay",
  "MMMM yyy": "MonthLongYear",
  "MMM d, yyy": "MonthDayYear",
  "MMM d, yyyy, h:mm a": "MonthDayYearTimeShort",
  "MMM d, y, h:mm a": "MonthDayYearTimeShort",
  "hh:mm a": "TimeShort",
  "HH:mm a": "TimeShort",
  "M/d/yyy": "NumericDate",
  // NOTE: "MMM", "MMMM", and "MM/dd/yyyy hh:mm a z" are intentionally NOT bridged.
  // "MMM"/"MMMM" are reused by getSlackChannelName.ts to build a Slack channel identifier
  // (lowercased, stripped to alphanumerics, length-capped, sent to the Slack API) — a MACHINE
  // use, not display. "MM/dd/yyyy hh:mm a z" is used exclusively in the MetricsPage chart
  // download paths that feed exportFromJSON({ exportType: "csv" }) — a CSV data export, also
  // MACHINE. Bridging any of them (the bridge is keyed by pattern string) would alter those
  // non-display outputs across locales.
  // NOTE: "dd MMM yyyy" was previously used as an ApexCharts `tooltip.x.format` config string.
  // Those usages have been replaced with Intl.DateTimeFormat formatters for locale support.
  // The pattern is no longer present in the codebase.
  // NOTE: "yyy" is intentionally NOT bridged. In UpcomingEventsView/Filters/DateBar.tsx the
  // formatted year is assigned to startYear/endYear and used in equality comparisons
  // (`startYear === endYear`) that drive label-shape branching — a MACHINE use, not display.
  // NOTE: "MMMM d, yyyy · hh:mm a" is intentionally NOT bridged. The `·` separator and
  // zero-padded `hh` are deliberate styling that Intl can't reproduce (it substitutes the
  // locale connector, e.g. "at", and drops the leading zero). Leaving it unbridged keeps the
  // raw date-fns output unchanged; localizing these callsites is deferred to the lint sweep.
});
